import React, { useState, useEffect } from 'react';
import { masterSpareParts } from '../services/api';
import Pagination from '../components/Pagination';

const GlobalMasterSpareParts = () => {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    last_page: 1,
    per_page: 50,
    current_page: 1,
    from: 0,
    to: 0
  });
  
  const [formData, setFormData] = useState({
    part_number: '',
    part_name: '',
    price: ''
  });
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchData(1);
  }, []);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        per_page: 50
      };
      if (searchQuery) params.search = searchQuery;

      const res = await masterSpareParts.getAll(params);
      setDataList(res.data.data);
      setPagination(res.data.pagination);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch master spare parts', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setCurrentPage(1);
      fetchData(1);
      return;
    }
    setCurrentPage(1);
    fetchData(1);
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        part_number: item.part_number,
        part_name: item.part_name,
        price: item.price || ''
      });
    } else {
      setEditingItem(null);
      setFormData({ part_number: '', part_name: '', price: '' });
    }
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    try {
      const payload = {
        part_number: formData.part_number.trim(),
        part_name: formData.part_name.trim(),
        price: formData.price ? parseInt(formData.price) : null
      };

      if (editingItem) {
        await masterSpareParts.update(editingItem.id, payload);
      } else {
        await masterSpareParts.create(payload);
      }
      setShowModal(false);
      fetchData(currentPage);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan data.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus global master spare part ini? Data dalam mapping mungkin terpengaruh.')) return;
    try {
      await masterSpareParts.delete(id);
      fetchData(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal menghapus data.');
    }
  };

  const filteredData = dataList;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Global Master Spare Parts</h2>
          <p className="text-gray-500 text-sm mt-1">Kelola perpustakaan global spare part dengan nomor bagian, nama, dan harga.</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-blue-700 transition shadow-sm"
        >
          + Tambah Part
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <input
          type="text"
          placeholder="Cari nomor bagian atau nama part..."
          value={searchQuery}
          onChange={(e) => {
            const query = e.target.value;
            setSearchQuery(query);
            handleSearch(query);
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
        />
        {pagination.total > 0 && (
          <p className="text-xs text-gray-500 mt-2">
            Menampilkan {pagination.from} - {pagination.to} dari {pagination.total} data (Halaman {pagination.current_page} dari {pagination.last_page})
          </p>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nomor Bagian</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nama Part</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Harga</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Aksi</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-500 font-medium">Memuat data...</td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-500 font-medium">Belum ada data global master spare part.</td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                        {item.part_number}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">{item.part_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {item.price > 0 ? `Rp ${item.price.toLocaleString('id-ID')}` : <span className="text-orange-600 font-bold">Diskontinyu</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button onClick={() => handleOpenModal(item)} className="text-blue-600 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg mr-2 transition">Edit</button>
                      <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900 bg-red-50 px-3 py-1.5 rounded-lg transition">Hapus</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination 
          pagination={pagination} 
          onPageChange={(page) => fetchData(page)} 
        />
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">{editingItem ? 'Edit Global Master Part' : 'Tambah Global Master Part Baru'}</h3>
              <p className="text-sm text-gray-500 mt-1">Isi formulir di bawah ini untuk mengelola global master spare part.</p>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm font-medium border border-red-100">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nomor Bagian (Part Number)</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.part_number}
                  onChange={e => setFormData({ ...formData, part_number: e.target.value })}
                  placeholder="Contoh: BAN-DEPAN-001"
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nama Spare Part</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.part_name}
                  onChange={e => setFormData({ ...formData, part_name: e.target.value })}
                  placeholder="Contoh: Ban Depan Standar"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Harga (Kosongkan jika diskontinyu)</label>
                <input
                  type="number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.price}
                  onChange={e => setFormData({ ...formData, price: e.target.value })}
                  placeholder="Contoh: 1500000"
                  min="0"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {isSaving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalMasterSpareParts;
